import { useLayoutEffect, useRef, useState } from 'react';
import { FrontendVisibility } from '../models/ISettingsFrontend';

type ChangeVisibility = (data: {
  projectId: number;
  phaseId: number;
  visibility: FrontendVisibility;
}) => Promise<boolean | void>;

interface UseLaneVisibilityParams {
  serverVisibility: FrontendVisibility;
  projectId: number;
  phaseId: number;
  changeVisibilityOfLanePhase: ChangeVisibility;
}

export const useLaneVisibility = ({
  serverVisibility,
  projectId,
  phaseId,
  changeVisibilityOfLanePhase,
}: UseLaneVisibilityParams) => {
  const [laneVisibility, setLaneVisibility] = useState(serverVisibility);
  const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);
  const visibilityButtonRef = useRef<HTMLButtonElement>(null);
  const isLaneCollapsed = laneVisibility === FrontendVisibility.Close;

  useLayoutEffect(() => {
    setLaneVisibility(serverVisibility);
  }, [serverVisibility]);

  useLayoutEffect(() => {
    const lane = visibilityButtonRef.current?.closest('section');
    if (!lane) return;

    lane.classList.toggle('visibility-lane__close', isLaneCollapsed);
    lane.classList.toggle('visibility-lane__open', !isLaneCollapsed);
  }, [isLaneCollapsed]);

  const toggleLaneVisibility = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isVisibilityUpdating) return;

    const previousVisibility = laneVisibility;
    const nextVisibility =
      laneVisibility === FrontendVisibility.Close
        ? FrontendVisibility.Open
        : FrontendVisibility.Close;

    setLaneVisibility(nextVisibility);
    setIsVisibilityUpdating(true);

    try {
      const updated = await changeVisibilityOfLanePhase({
        projectId,
        phaseId,
        visibility: nextVisibility,
      });
      if (updated === false) setLaneVisibility(previousVisibility);
    } finally {
      setIsVisibilityUpdating(false);
    }
  };

  return {
    isLaneCollapsed,
    isVisibilityUpdating,
    visibilityButtonRef,
    toggleLaneVisibility,
  };
};
